import React, { useEffect, useState } from "react";
import { Upload } from "antd";
import {
    AlertTriangle,
    CheckCircle2,
    CloudUpload,
    FileCheck2,
    FileText,
    Gauge,
    Loader2,
    ScanText,
    ShieldCheck,
    Sparkles,
    Target,
    X,
} from "lucide-react";
import {
    RESUME_ACCEPT,
    RESUME_FORMATS_LABEL,
    RESUME_MAX_SIZE_MB,
    RESUME_USES,
} from "./resumeVerification";

const USE_ICONS = {
    'role-scores': Target,
    'hire-confidence': Gauge,
    'ai-scoring': Sparkles,
};

const formatFileSize = (bytes = 0) => {
    if (bytes >= 1024 * 1024) {
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    }
    return `${Math.max(1, Math.round(bytes / 1024))} KB`;
};

/**
 * Explains why a readable resume is required.
 */
export const ResumeRequirementExplainer = ({ className = '' }) => (
    <div className={`rounded-lg border border-primary/15 bg-[#F7F4FD] p-4 ${className}`}>
        <div className="flex items-start gap-3">
            <ShieldCheck className="h-5 w-5 shrink-0 text-primary mt-0.5" aria-hidden />
            <div>
                <p className="m-0 text-[15px] font-semibold text-gray-900">
                    A readable resume is required to join the Wripple Marketplace
                </p>
                <p className="m-0 mt-1 text-sm text-gray-600">
                    Our AI reads your resume to power the scores clients see and the projects
                    you're matched to:
                </p>
            </div>
        </div>

        <ul className="m-0 mt-3 grid list-none gap-2 p-0 xl:grid-cols-3">
            {RESUME_USES.map(use => {
                const Icon = USE_ICONS[use.key];
                return (
                    <li key={use.key} className="rounded-md bg-white p-3 shadow-sm">
                        <div className="flex items-center gap-2">
                            <Icon className="h-4 w-4 shrink-0 text-primary" aria-hidden />
                            <span className="text-sm font-semibold text-gray-900">{use.title}</span>
                        </div>
                        <p className="m-0 mt-1 text-xs leading-snug text-gray-600">{use.description}</p>
                    </li>
                );
            })}
        </ul>
    </div>
);

/**
 * Pick a resume file. Shows the chosen file once selected.
 */
export const ResumeDropzone = ({ file, onSelect, onRemove, disabled = false, prompt = 'Drag your resume here or' }) => {
    if (file) {
        return (
            <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white p-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-[#F7F4FD]">
                    <FileText className="h-5 w-5 text-primary" aria-hidden />
                </div>
                <div className="min-w-0 flex-1">
                    <p className="m-0 truncate text-sm font-medium text-gray-900">{file.name}</p>
                    <p className="m-0 text-xs text-gray-500">{formatFileSize(file.size)}</p>
                </div>
                {onRemove && (
                    <button
                        type="button"
                        onClick={onRemove}
                        disabled={disabled}
                        aria-label="Remove file"
                        className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-800">
                        <X className="h-4 w-4" aria-hidden />
                    </button>
                )}
            </div>
        );
    }

    return (
        <Upload.Dragger
            accept={RESUME_ACCEPT}
            multiple={false}
            showUploadList={false}
            disabled={disabled}
            beforeUpload={selected => {
                onSelect(selected);
                return false; // we upload it ourselves
            }}
            className="resume-verification-dropzone">
            <div className="flex flex-col items-center gap-2 px-4 py-3">
                <CloudUpload className="h-8 w-8 text-primary" aria-hidden />
                <p className="m-0 text-sm text-gray-800">
                    {prompt} <span className="font-semibold text-primary">browse</span>
                </p>
                <p className="m-0 text-xs text-gray-500">
                    {RESUME_FORMATS_LABEL} &middot; up to {RESUME_MAX_SIZE_MB} MB
                </p>
            </div>
        </Upload.Dragger>
    );
};

const CHECKING_STEPS = [
    { label: 'Uploading your file', startsAt: 0 },
    { label: 'Reading your document', hint: 'Scans and images take a little longer', startsAt: 2500 },
    { label: "Confirming it's a resume", hint: 'Looking for your work history and skills', startsAt: 9000 },
];

const SLOW_CHECK_MS = 25000;

/**
 * Progress shown while the server checks the resume. The request is a
 * single call, so steps advance on a timer and the last one stays active
 * until the response arrives.
 */
export const ResumeCheckingProgress = ({ fileName, title = 'Checking your resume' }) => {
    const [elapsed, setElapsed] = useState(0);

    useEffect(() => {
        const startedAt = Date.now();
        const timer = setInterval(() => setElapsed(Date.now() - startedAt), 400);
        return () => clearInterval(timer);
    }, []);

    const activeIndex = CHECKING_STEPS.reduce((active, step, index) => (
        elapsed >= step.startsAt ? index : active
    ), 0);

    // Eases toward 90% so the bar keeps moving without claiming to be done
    const progress = 90 * (1 - Math.exp(-elapsed / 15000));

    return (
        <div className="py-2" role="status" aria-live="polite">
            <div className="flex flex-col items-center text-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#F7F4FD]">
                    <ScanText className="h-6 w-6 text-primary" aria-hidden />
                </div>
                <h3 className="m-0 mt-3 text-xl font-semibold text-gray-900">{title}</h3>
                <p className="m-0 mt-1 text-sm text-gray-600">
                    We're making sure your resume can be read and scored.
                </p>
                {fileName && (
                    <span className="mt-3 inline-flex max-w-full items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1 text-xs text-gray-700">
                        <FileText className="h-3.5 w-3.5 shrink-0" aria-hidden />
                        <span className="truncate">{fileName}</span>
                    </span>
                )}
            </div>

            <div className="mx-auto mt-6 h-1.5 max-w-md overflow-hidden rounded-full bg-gray-100">
                <div
                    className="h-full rounded-full bg-primary transition-all duration-500 ease-out"
                    style={{ width: `${progress}%` }}
                />
            </div>

            <ol className="mx-auto mt-6 max-w-md list-none space-y-3 p-0">
                {CHECKING_STEPS.map((step, index) => {
                    const isDone = index < activeIndex;
                    const isActive = index === activeIndex;
                    return (
                        <li key={step.label} className="flex items-start gap-3">
                            {isDone && <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[#22C55E]" aria-hidden />}
                            {isActive && <Loader2 className="mt-0.5 h-5 w-5 shrink-0 animate-spin text-primary" aria-hidden />}
                            {!isDone && !isActive && <span className="mt-0.5 h-5 w-5 shrink-0 rounded-full border-2 border-gray-300" aria-hidden />}
                            <div>
                                <p className={`m-0 text-sm ${isActive ? 'font-medium text-primary' : isDone ? 'text-gray-700' : 'text-gray-400'}`}>
                                    {step.label}
                                </p>
                                {isActive && step.hint && (
                                    <p className="m-0 text-xs text-gray-500">{step.hint}</p>
                                )}
                            </div>
                        </li>
                    );
                })}
            </ol>

            <p className="m-0 mt-6 text-center text-xs text-gray-500">
                {elapsed > SLOW_CHECK_MS
                    ? 'Still working. Long or scanned resumes can take a minute or two. Please keep this window open.'
                    : 'This usually takes less than a minute. Please keep this window open.'}
            </p>
        </div>
    );
};

/**
 * Explains why a resume didn't pass and what to try next.
 *
 * @param {ResumeVerificationError} error
 * @param {Boolean} hasExistingResume
 */
export const ResumeFailureNotice = ({ error, hasExistingResume = false, fileName }) => {
    const { title, summary, tips } = error.feedback;
    const showDetail = error.reason === 'not_a_resume' && error.detail;

    return (
        <div role="alert">
            <div className="flex items-start gap-3 rounded-lg border border-[#F5C2CB] bg-[#FDF2F4] p-4">
                <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-[#E54963]" aria-hidden />
                <div className="min-w-0">
                    <p className="m-0 font-semibold text-gray-900">{title}</p>
                    {fileName && <p className="m-0 truncate text-xs text-gray-500">{fileName}</p>}
                    <p className="m-0 mt-1 text-sm text-gray-700">{summary}</p>
                    {showDetail && (
                        <p className="m-0 mt-2 text-sm text-gray-700">
                            {error.detail}
                        </p>
                    )}
                </div>
            </div>

            <p className="m-0 mb-2 mt-4 text-sm font-semibold text-gray-900">Things to try</p>
            <ul className="m-0 list-none space-y-2 p-0">
                {tips.map(tip => (
                    <li key={tip} className="flex items-start gap-2 text-sm text-gray-700">
                        <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" aria-hidden />
                        <span>{tip}</span>
                    </li>
                ))}
            </ul>

            {hasExistingResume && (
                <p className="m-0 mt-4 flex items-center gap-1.5 text-xs text-gray-500">
                    <FileCheck2 className="h-3.5 w-3.5 shrink-0" aria-hidden />
                    Your current resume is still on file and hasn't changed.
                </p>
            )}
        </div>
    );
};

/**
 * Confirmation once the resume passes.
 */
export const ResumeVerifiedNotice = ({ fileName, children }) => (
    <div className="flex flex-col items-center py-4 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#E9FBF0]">
            <CheckCircle2 className="h-9 w-9 text-[#22C55E]" aria-hidden />
        </div>
        <h3 className="m-0 mt-4 text-xl font-semibold text-gray-900">Resume verified</h3>
        {fileName && (
            <p className="m-0 mt-1 max-w-full truncate text-sm text-gray-500">{fileName}</p>
        )}
        <p className="m-0 mt-3 max-w-md text-sm text-gray-700">
            {children || "We can read your resume and it's saved to your profile. You're all set to keep going."}
        </p>
    </div>
);
